import React from 'react';
import { ClipboardCheck, ShieldCheck, Send, CheckCircle } from 'lucide-react';

export default function HowItWorks() {
  const steps = [
    {
      icon: ClipboardCheck,
      title: 'Select Assessment',
      description: 'Choose the privacy risk assessment that best fits your needs and complete the secure checkout process.',
      color: 'from-emerald-400 to-emerald-600'
    },
    {
      icon: ShieldCheck,
      title: 'Receive Access',
      description: 'Receive via email a link that will direct you to your assessment form. Use the code in the email to access your assessment form.',
      color: 'from-teal-400 to-teal-600'
    },
    {
      icon: Send,
      title: 'Submit Questionnaire',
      description: 'Review and submit your completed assessment form. Our experts will review the information and begin the risk assessment.',
      color: 'from-cyan-400 to-cyan-600'
    },
    {
      icon: CheckCircle,
      title: 'Get Results',
      description: 'Receive email notification of assessment completion along with copies of final risk reports and documents included in your plan.',
      color: 'from-brand-light to-brand-primary'
    }
  ];

  return (
    <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 py-24" id="how-it-works">
      {/* Background pattern */}
      <div 
        className="absolute inset-0 z-0 opacity-[0.07]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23059669' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block">
            <h2 className="text-3xl font-bold mb-4 relative font-montserrat text-gray-700">
              How It Works
              {/* Decorative underline */}
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-75" />
            </h2>
          </div>
          <p className="text-lg text-gray-600 mt-6">
            Get started with your privacy assessment in four simple steps.  Privacy knowledge or expertise is not required.
          </p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-200 via-teal-200 to-cyan-200 transform -translate-y-1/2 hidden md:block" />
          
          <div className="grid md:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <div 
                key={index} 
                className="group relative bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Step number */}
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center border-2 border-brand-primary text-brand-primary font-semibold">
                  {index + 1}
                </div>

                <div className="text-center">
                  {/* Icon container with gradient background */}
                  <div className="inline-flex mb-6 rounded-xl p-0.5 bg-gradient-to-br group-hover:scale-110 transition-transform duration-300">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${step.color} p-4`}>
                      <step.icon className="w-full h-full text-white" />
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold mb-3 group-hover:text-brand-primary transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </div>

                {/* Decorative dots */}
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  <div className="w-1 h-1 rounded-full bg-brand-primary/30" />
                  <div className="w-1 h-1 rounded-full bg-brand-primary/60" />
                  <div className="w-1 h-1 rounded-full bg-brand-primary" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Smartsheet Partnership Section */}
        <div className="mt-20">
          <div className="bg-white rounded-2xl p-8 shadow-lg relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-transparent to-emerald-50/30" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-brand-primary/10 to-transparent rounded-full transform translate-x-1/2 -translate-y-1/2 blur-3xl" />
            
            <div className="relative max-w-3xl mx-auto text-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-gray-900">
                  Assessments and Reports Powered by AI
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  Our innovative platform utilizes cloud-based assessment forms in combination with Artificial Intelligence for conducting analyses and creating reports. Assessment analyses and reports generated by our AI platform are reviewed by our certified privacy experts prior to delivery.
                </p>
              
              </div>

              {/* Decorative elements */}
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-emerald-100 to-transparent rounded-full transform -translate-x-1/2 translate-y-1/2 blur-2xl" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}